import React from 'react';
import { AlertTriangle } from 'lucide-react';
import { TYPO, R, S, SHADCN, softTone } from '../theme';
import Card from './Card';
import StatusPill from './StatusPill';
import { conseilVirement, FENETRE_SALAIRES } from '../virement';

// ── <VirementConseille> — combien garder sur BNP, combien virer vers Revolut.
//
// Le chiffre principal est ce qui doit RESTER sur BNP : ce compte porte tous les
// prélèvements récurrents et doit les couvrir jusqu'à la fin du mois. Le virable
// n'est que le reliquat. Une carte organisée autour du virement du jour
// répondait à la mauvaise question et multipliait les gestes.
//
// La règle vit dans src/virement.js (testée par scripts/recette-virement.js) ;
// ce composant ne fait que l'afficher.

const eur = (v) => new Intl.NumberFormat('fr-FR', {
  style: 'currency', currency: 'EUR', maximumFractionDigits: 0,
}).format(v);

export default function VirementConseille({ soldeBNP, soldeRevolut, jour, dateSoldeBNP, echeances = [], style }) {
  // Sans le solde Revolut (sync coupée, API muette), toute recommandation serait
  // une devinette : mieux vaut ne rien afficher que conseiller à l'aveugle.
  if (!Number.isFinite(soldeBNP) || !Number.isFinite(soldeRevolut)) return null;

  // Le solde BNP vient du dernier import CSV : périmé, il fausse le conseil sans
  // le dire. On affiche son âge, et on le signale au-delà de 3 jours.
  const joursBNP = dateSoldeBNP
    ? Math.floor((Date.now() - new Date(dateSoldeBNP + 'T12:00:00').getTime()) / 86400000)
    : null;
  const perime = joursBNP !== null && joursBNP > 3;

  const c = conseilVirement({ jour, soldeBNP, soldeRevolut, echeances });
  const serre = c.virable <= 0;
  const salairesAVenir = echeances.find(e => e.compte === 'Revolut' && !e.paye);

  return (
    <Card style={{ marginBottom: S[4], ...style }}>
      {/* ── Ce qui doit rester sur BNP : le chiffre qui décide de tout ── */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: S[4], flexWrap: 'wrap' }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ ...TYPO.eyebrow, color: SHADCN.mutedFg, marginBottom: S[2] }}>À garder sur BNP</div>
          <div style={{ ...TYPO.num, fontSize: 34, fontWeight: 700, lineHeight: 1, color: SHADCN.fg }}>
            {eur(c.aGarder)}
          </div>
          <div style={{ ...TYPO.caption, color: SHADCN.mutedFg, marginTop: S[2] }}>
            {eur(c.resteCharges)} de prélèvements encore à sortir + {eur(c.plancher)} d'imprévu
          </div>
        </div>

        <div style={{ textAlign: 'right' }}>
          <div style={{ ...TYPO.eyebrow, color: SHADCN.mutedFg, marginBottom: S[2] }}>Disponible à virer</div>
          <div style={{ ...TYPO.num, fontSize: 28, fontWeight: 700, lineHeight: 1, color: serre ? SHADCN.error : SHADCN.success }}>
            {eur(c.virable)}
          </div>
          <div style={{ ...TYPO.caption, ...TYPO.num, color: SHADCN.mutedFg, marginTop: S[2] }}>
            BNP {eur(soldeBNP)}
            {joursBNP !== null && (
              <span style={{ color: perime ? SHADCN.warning : SHADCN.mutedFg }}>
                {' '}({joursBNP <= 0 ? "aujourd'hui" : joursBNP === 1 ? 'hier' : `il y a ${joursBNP} j`})
              </span>
            )}
          </div>
        </div>
      </div>

      {/* ── L'action du jour ── */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: S[3], flexWrap: 'wrap',
        marginTop: S[4], paddingTop: S[3], borderTop: `1px solid ${SHADCN.border}`,
      }}>
        <div style={{ ...TYPO.body, color: SHADCN.fg, minWidth: 0 }}>
          {c.action === 'virer' ? (
            <>
              Virer <strong style={{ ...TYPO.num }}>{eur(c.montant)}</strong> vers Revolut
              <span style={{ color: SHADCN.mutedFg }}> — {c.motif}</span>
            </>
          ) : (
            <>
              Rien à virer
              <span style={{ color: SHADCN.mutedFg }}>
                {' '}· Revolut à {eur(soldeRevolut)}
                {!c.fenetreSalaires && ` · prochain rendez-vous le ${FENETRE_SALAIRES.du} (${FENETRE_SALAIRES.motif})`}
              </span>
            </>
          )}
        </div>
        <StatusPill variant={c.alerte ? 'error' : c.action === 'virer' ? 'warning' : 'neutral'} dot>
          {c.alerte ? 'Salaires menacés' : c.action === 'virer' ? 'À virer' : 'À jour'}
        </StatusPill>
      </div>

      {perime && (
        <div style={{ ...TYPO.caption, color: SHADCN.warning, marginTop: S[3] }}>
          Solde BNP vieux de {joursBNP} jours — importer le relevé pour fiabiliser le conseil.
        </div>
      )}

      {c.plafonne && !c.alerte && (
        <div style={{ ...TYPO.caption, color: SHADCN.warning, marginTop: S[3] }}>
          Virement limité par les prélèvements à venir : il manque {eur(c.manque - c.montant)} pour atteindre {eur(c.cible)}.
        </div>
      )}

      {c.alerte && (
        <div style={{
          ...TYPO.caption, color: SHADCN.error, marginTop: S[3], display: 'flex', gap: S[2],
          alignItems: 'flex-start', background: softTone(SHADCN.error).bg,
          border: `1px solid ${softTone(SHADCN.error).border}`, borderRadius: R.sm, padding: '8px 10px',
        }}>
          <AlertTriangle size={14} strokeWidth={2} style={{ flexShrink: 0, marginTop: 1 }} />
          <span>{c.alerte}</span>
        </div>
      )}

      {/* ── Ce qui sort encore de BNP ce mois-ci ──────────────────────────────
          Le détail du chiffre du haut : les jours viennent des prélèvements
          réellement observés en banque, pas d'une liste saisie. */}
      {c.lignes.length > 0 && (
        <div style={{ marginTop: S[4], borderTop: `1px solid ${SHADCN.border}`, paddingTop: S[3] }}>
          <div style={{ ...TYPO.eyebrow, color: SHADCN.mutedFg, marginBottom: S[2] }}>
            Encore à sortir de BNP · {c.lignes.length} prélèvement{c.lignes.length > 1 ? 's' : ''}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))', gap: `2px ${S[5]}px` }}>
            {c.lignes.map((e, i) => {
              const enRetard = e.jour < jour;
              return (
                <div key={i} style={{ display: 'flex', alignItems: 'baseline', gap: S[2], padding: '3px 0' }}>
                  <span style={{
                    ...TYPO.caption, ...TYPO.num, width: 26, flexShrink: 0, textAlign: 'right',
                    color: enRetard ? SHADCN.warning : SHADCN.mutedFg,
                  }}>{e.jour}</span>
                  <span style={{
                    ...TYPO.caption, flex: 1, minWidth: 0, color: SHADCN.fg,
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>
                    {e.label}{enRetard && <span style={{ color: SHADCN.warning }}> · attendu</span>}
                  </span>
                  <span style={{ ...TYPO.caption, ...TYPO.num, color: SHADCN.mutedFg, flexShrink: 0 }}>{eur(e.montant)}</span>
                </div>
              );
            })}
          </div>
          {salairesAVenir && (
            <div style={{ ...TYPO.caption, color: SHADCN.mutedFg, marginTop: S[2], fontSize: 11 }}>
              Hors BNP : {eur(salairesAVenir.montant)} de salaires partent de Revolut le {salairesAVenir.jour}.
            </div>
          )}
        </div>
      )}
    </Card>
  );
}
